import {
  getDataFromServer,
  postDataToServerWithFullResponse,
  deleteDataFromServerWithFullResponse,
  throwIfDeleteFailed
} from '../../../util';

class ThresholdGenerationModule {
  constructor(baseURL, httpHeaders) {
    this.baseURL = baseURL;
    this.httpHeaders = httpHeaders;
  }

  triggerThresholdGeneration = async (payload) => {
    const { scenarioId, config } = payload;
    const url = `${this.baseURL}/scenarios/${scenarioId}/thresholdGeneration`;
    // Use the full-response helper: the axios instance sets validateStatus:false,
    // so a 5xx/4xx resolves instead of rejecting. The plain postDataToServer returns
    // only res.data (the body), which has no HTTP status, so a failure would slip
    // through as a "success" and the UI would show a phantom "generating" banner.
    const response = await postDataToServerWithFullResponse(url, config);
    const httpStatus = response?.status ?? response?.response?.status;
    if (httpStatus !== 200 && httpStatus !== 201) {
      throw new Error(
        response?.data?.message ||
          response?.response?.data?.message ||
          response?.message ||
          'Failed to trigger threshold generation'
      );
    }
    return response.data;
  };

  getThresholdStatus = async (payload) => {
    const { scenarioId } = payload;
    const url = `${this.baseURL}/scenarios/${scenarioId}/thresholdGeneration/status`;
    const response = await getDataFromServer(url, this.httpHeaders);
    // FIN-12167 (banner-survives-refresh): getDataFromServer returns the response BODY on success
    // and an Error object on failure. The status body is `{ status: 'none'|'RUNNING'|'COMPLETED' }`,
    // so the old `response.status !== 200` check mistook the body's `status` field for an HTTP status
    // and threw on EVERY successful poll — FETCH_THRESHOLD_STATUS always failed, so the "generating"
    // banner never rehydrated on refresh. Treat only an actual axios Error as a failure.
    if (response instanceof Error) {
      throw new Error(
        response?.response?.data?.message || 'Failed to fetch threshold status'
      );
    }
    return response;
  };

  // FIN-12858 — same dead guard as deleteScoringConfig: `deleteDataFromServer` returns the
  // BODY, so `response.status` was always undefined and a failed discard reported success.
  discardThresholdGeneration = async (payload) => {
    const { scenarioId } = payload;
    const url = `${this.baseURL}/scenarios/${scenarioId}/thresholdGeneration`;
    return throwIfDeleteFailed(
      await deleteDataFromServerWithFullResponse(url),
      'Failed to discard threshold generation'
    );
  };

  getThresholdResults = async (payload) => {
    const { scenarioId } = payload;
    const url = `${this.baseURL}/scenarios/${scenarioId}/autoThreshold`;
    const response = await getDataFromServer(url, this.httpHeaders);
    if (response && response.status && response.status !== 200) {
      throw new Error(
        response?.data?.message || 'Failed to fetch threshold results'
      );
    }
    return response?.data || response;
  };

  getThresholdConfig = async (payload) => {
    const { scenarioId } = payload;
    const url = `${this.baseURL}/scenarios/${scenarioId}/autoThresholdConfig`;
    const response = await getDataFromServer(url, this.httpHeaders);
    if (response && response.status && response.status !== 200) {
      throw new Error(
        response?.data?.message || 'Failed to fetch threshold config'
      );
    }
    return response?.data || response;
  };
}

export default ThresholdGenerationModule;
